import type { PageHelp } from '../../help/types';

/** Page guide for `/settings/locales` (spec §9.5, §5.4). */
export const localesHelp: PageHelp = {
  title: 'Locales',
  intro:
    'Every language Glossa knows, with the plural forms its exports rely on and the legacy spellings it still answers to. Adding a locale here makes it available; a project starts using it once it is enabled in that project’s settings.',
  sections: ['The catalogue', 'Add locale', 'Aliases'],
  contexts: {
    'add-dialog': 'Click “Add locale” at the top right.',
    'aliases-dialog': 'Click “Aliases” at the end of a locale’s row.',
  },
  steps: [
    // --- The catalogue ---------------------------------------------------------------------
    {
      selector: '[data-help="locales-list"]',
      title: 'Read the catalogue',
      body: 'Every locale with its code, English and native name, plural forms, aliases, writing direction and whether it is active. Codes are correct BCP 47 with the script before the region, such as `sr-Latn-RS`.',
      section: 'The catalogue',
      i18n: {
        'de-AT': {
          title: 'Katalog lesen',
          body: 'Jede Locale mit Code, englischem und eigensprachlichem Namen, Pluralformen, Aliasen, Schreibrichtung und der Angabe, ob sie aktiv ist. Codes sind korrektes BCP 47 mit der Schrift vor der Region, etwa `sr-Latn-RS`.',
        },
        'sr-Latn-RS': {
          title: 'Pregledajte katalog',
          body: 'Svaki locale sa kodom, engleskim i izvornim nazivom, plural formama, aliasima, smerom pisanja i podatkom da li je aktivan. Kodovi su ispravan BCP 47 sa pismom ispred regiona, na primer `sr-Latn-RS`.',
        },
      },
    },
    {
      selector: '[data-help="locales-search"]',
      title: 'Find a locale',
      body: 'Filters the list as you type, by code, English or native name, or alias. Any part matches, regardless of upper or lower case, so `sr-RS-Latn` finds the locale that answers to that old spelling.',
      section: 'The catalogue',
      i18n: {
        'de-AT': {
          title: 'Locale finden',
          body: 'Filtert die Liste während der Eingabe nach Code, englischem oder eigensprachlichem Namen oder Alias. Jeder Teil passt, unabhängig von Groß- und Kleinschreibung, daher findet `sr-RS-Latn` die Locale, die auf diese alte Schreibweise hört.',
        },
        'sr-Latn-RS': {
          title: 'Pronađite locale',
          body: 'Filtrira listu dok kucate, po kodu, engleskom ili izvornom nazivu, ili aliasu. Poklapa se bilo koji deo, bez obzira na velika i mala slova, pa `sr-RS-Latn` pronalazi locale koji odgovara na taj stari zapis.',
        },
      },
    },
    {
      selector: '[data-help="locales-plural-forms"]',
      title: 'Check the plural forms',
      body: 'Taken from `Intl.PluralRules`, never typed in by hand: the export writes one `key_form` entry per form, and an invented set would break i18next for that language. Every locale has at least `other`.',
      section: 'The catalogue',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Pluralformen prüfen',
          body: 'Kommen aus `Intl.PluralRules` und werden nie von Hand eingetragen: Der Export schreibt pro Form einen Eintrag `key_form`, und ein erfundener Satz würde i18next für diese Sprache brechen. Jede Locale hat mindestens `other`.',
        },
        'sr-Latn-RS': {
          title: 'Proverite plural forme',
          body: 'Dolaze iz `Intl.PluralRules` i nikad se ne unose ručno: export upisuje po jedan unos `key_form` za svaku formu, a izmišljen skup bi pokvario i18next za taj jezik. Svaki locale ima bar `other`.',
        },
      },
    },
    {
      selector: '[data-help="locales-aliases"]',
      title: 'Read the aliases',
      body: 'The old spellings a locale still answers to, such as Phrase’s `sr-RS-Latn`. They are matched on import and by the Phrase-compat endpoint, and never written into an export.',
      section: 'The catalogue',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Aliase lesen',
          body: 'Die alten Schreibweisen, auf die eine Locale weiterhin hört, etwa `sr-RS-Latn` aus Phrase. Sie werden beim Import und vom Phrase-kompatiblen Endpunkt erkannt und nie in einen Export geschrieben.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte aliase',
          body: 'Stari zapisi na koje locale i dalje odgovara, na primer `sr-RS-Latn` iz Phrase-a. Prepoznaju se pri uvozu i na Phrase-kompatibilnom endpoint-u, a nikad se ne upisuju u export.',
        },
      },
    },
    {
      selector: '[data-help="locales-rtl"]',
      title: 'See which locales are written right to left',
      body: '“yes” marks a right-to-left language. It is set when the locale is added, from the language part of the code: Arabic, Hebrew, Persian and Urdu.',
      section: 'The catalogue',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Sehen, welche Locales von rechts nach links laufen',
          body: '„yes“ markiert eine Sprache, die von rechts nach links geschrieben wird. Das wird beim Anlegen der Locale aus dem Sprachteil des Codes gesetzt: Arabisch, Hebräisch, Persisch und Urdu.',
        },
        'sr-Latn-RS': {
          title: 'Pogledajte koji se locale-i pišu zdesna nalevo',
          body: '„yes“ označava jezik koji se piše zdesna nalevo. Postavlja se pri dodavanju locale-a, na osnovu jezičkog dela koda: arapski, hebrejski, persijski i urdu.',
        },
      },
    },
    {
      selector: '[data-help="locales-edit-aliases"]',
      title: 'Edit the aliases of a locale',
      body: 'Opens the alias editor for this locale. Use it when an old build or a file exported from Phrase still uses a legacy spelling of the code.',
      section: 'The catalogue',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Aliase einer Locale bearbeiten',
          body: 'Öffnet den Alias-Editor für diese Locale. Verwenden Sie ihn, wenn ein alter Build oder eine aus Phrase exportierte Datei noch eine alte Schreibweise des Codes verwendet.',
        },
        'sr-Latn-RS': {
          title: 'Izmenite aliase locale-a',
          body: 'Otvara uređivanje aliasa za ovaj locale. Koristite ga kada stari build ili fajl izvezen iz Phrase-a i dalje koristi stari zapis koda.',
        },
      },
    },

    // --- Add locale ------------------------------------------------------------------------
    {
      selector: '[data-help="locales-add"]',
      title: 'Add a locale',
      body: 'Opens the “Add locale” dialog. You only type the code; the names and plural forms are derived from it in your browser.',
      section: 'Add locale',
      i18n: {
        'de-AT': {
          title: 'Locale hinzufügen',
          body: 'Öffnet den Dialog „Add locale“. Sie tippen nur den Code ein; Namen und Pluralformen leitet Ihr Browser daraus ab.',
        },
        'sr-Latn-RS': {
          title: 'Dodajte locale',
          body: 'Otvara dijalog „Add locale“. Kucate samo kod; nazive i plural forme iz njega izvodi vaš pregledač.',
        },
      },
    },
    {
      selector: '[data-help="locales-code"]',
      title: 'Type the BCP 47 code',
      body: 'Script before region — `sr-Latn-RS`, not `sr-RS-Latn`. The server normalises the casing and refuses a malformed tag or a code that already exists. Legacy spellings belong in aliases, not here.',
      section: 'Add locale',
      context: 'add-dialog',
      i18n: {
        'de-AT': {
          title: 'BCP-47-Code eingeben',
          body: 'Schrift vor Region – `sr-Latn-RS`, nicht `sr-RS-Latn`. Der Server vereinheitlicht die Groß- und Kleinschreibung und lehnt ein fehlerhaftes Tag oder einen schon vorhandenen Code ab. Alte Schreibweisen gehören zu den Aliasen, nicht hierher.',
        },
        'sr-Latn-RS': {
          title: 'Unesite BCP 47 kod',
          body: 'Pismo ispred regiona – `sr-Latn-RS`, a ne `sr-RS-Latn`. Server ujednačava velika i mala slova i odbija neispravan tag ili kod koji već postoji. Stari zapisi idu u aliase, ne ovde.',
        },
      },
    },
    {
      selector: '[data-help="locales-derived"]',
      title: 'Check what was derived',
      body: 'The plural forms from `Intl.PluralRules`, and the English and native names your browser knows for the code. If the browser does not recognise the tag, a warning appears instead and the locale cannot be added — check the spelling, because an invented tag would break the export for that language.',
      section: 'Add locale',
      context: 'add-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Abgeleitete Werte prüfen',
          body: 'Die Pluralformen aus `Intl.PluralRules` sowie der englische und der eigensprachliche Name, die Ihr Browser für den Code kennt. Erkennt der Browser das Tag nicht, erscheint stattdessen eine Warnung und die Locale kann nicht angelegt werden – prüfen Sie die Schreibweise, denn ein erfundenes Tag würde den Export für diese Sprache brechen.',
        },
        'sr-Latn-RS': {
          title: 'Proverite izvedene vrednosti',
          body: 'Plural forme iz `Intl.PluralRules` i engleski i izvorni naziv koje vaš pregledač zna za taj kod. Ako pregledač ne prepozna tag, umesto toga se pojavljuje upozorenje i locale ne može da se doda – proverite zapis, jer bi izmišljen tag pokvario export za taj jezik.',
        },
      },
    },
    {
      selector: '[data-help="locales-add-submit"]',
      title: 'Add the locale',
      body: 'Enabled only when plural forms could be derived. The new locale appears in the catalogue; enable it in a project’s settings to start translating into it.',
      section: 'Add locale',
      context: 'add-dialog',
      i18n: {
        'de-AT': {
          title: 'Locale anlegen',
          body: 'Nur aktiv, wenn sich Pluralformen ableiten ließen. Die neue Locale erscheint im Katalog; aktivieren Sie sie in den Einstellungen eines Projekts, um in sie zu übersetzen.',
        },
        'sr-Latn-RS': {
          title: 'Dodajte locale',
          body: 'Aktivno samo kada su plural forme mogle da se izvedu. Novi locale se pojavljuje u katalogu; uključite ga u podešavanjima projekta da biste počeli da prevodite na njega.',
        },
      },
    },

    // --- Aliases ---------------------------------------------------------------------------
    {
      selector: '[data-help="locales-alias-input"]',
      title: 'List the legacy spellings',
      body: 'Type the old spellings separated by commas, for example `sr-RS-Latn` for `sr-Latn-RS`. Underscores become hyphens and the casing is corrected, so `sr_rs_latn` is stored as `sr-RS-Latn`.',
      section: 'Aliases',
      context: 'aliases-dialog',
      i18n: {
        'de-AT': {
          title: 'Alte Schreibweisen auflisten',
          body: 'Geben Sie die alten Schreibweisen durch Beistriche getrennt ein, zum Beispiel `sr-RS-Latn` für `sr-Latn-RS`. Unterstriche werden zu Bindestrichen und die Groß- und Kleinschreibung wird korrigiert, `sr_rs_latn` wird also als `sr-RS-Latn` gespeichert.',
        },
        'sr-Latn-RS': {
          title: 'Navedite stare zapise',
          body: 'Unesite stare zapise odvojene zarezom, na primer `sr-RS-Latn` za `sr-Latn-RS`. Donje crte postaju crtice, a velika i mala slova se ispravljaju, pa se `sr_rs_latn` čuva kao `sr-RS-Latn`.',
        },
      },
    },
    {
      selector: '[data-help="locales-alias-preview"]',
      title: 'Check what will be saved',
      body: 'One chip per alias, in the spelling it will be stored as. A red chip is refused, and the message below says why: it is this locale’s own code, the code of another locale, or an alias another locale already holds (or longer than 20 characters). “Save” stays disabled until no chip is red.',
      section: 'Aliases',
      context: 'aliases-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Prüfen, was gespeichert wird',
          body: 'Ein Chip pro Alias, in der Schreibweise, in der er gespeichert wird. Ein roter Chip wird abgelehnt, und die Meldung darunter nennt den Grund: Er ist der eigene Code dieser Locale, der Code einer anderen Locale oder ein Alias, den schon eine andere Locale hat (oder länger als 20 Zeichen). „Save“ bleibt inaktiv, bis kein Chip mehr rot ist.',
        },
        'sr-Latn-RS': {
          title: 'Proverite šta će biti sačuvano',
          body: 'Po jedna oznaka za svaki alias, u zapisu u kome će biti sačuvan. Crvena oznaka se odbija, a poruka ispod kaže zašto: to je sopstveni kod ovog locale-a, kod drugog locale-a ili alias koji već ima drugi locale (ili je duži od 20 znakova). „Save“ ostaje neaktivno dok nijedna oznaka nije crvena.',
        },
      },
    },
    {
      selector: '[data-help="locales-alias-save"]',
      title: 'Save the aliases',
      body: 'The list you save replaces the stored one, so removing an alias here stops it resolving. Saving an empty list removes every alias, and imports and the Phrase-compat endpoint stop recognising the old spellings.',
      section: 'Aliases',
      context: 'aliases-dialog',
      i18n: {
        'de-AT': {
          title: 'Aliase speichern',
          body: 'Die gespeicherte Liste ersetzt die vorhandene – ein hier entfernter Alias wird also nicht mehr aufgelöst. Eine leere Liste entfernt alle Aliase, und Importe sowie der Phrase-kompatible Endpunkt erkennen die alten Schreibweisen nicht mehr.',
        },
        'sr-Latn-RS': {
          title: 'Sačuvajte aliase',
          body: 'Sačuvana lista zamenjuje postojeću, pa alias uklonjen ovde prestaje da se razrešava. Prazna lista uklanja sve aliase, i uvozi i Phrase-kompatibilni endpoint više ne prepoznaju stare zapise.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Locales',
      intro:
        'Jede Sprache, die Glossa kennt, mit den Pluralformen, auf die sich ihre Exporte stützen, und den alten Schreibweisen, auf die sie weiterhin hört. Eine hier angelegte Locale ist verfügbar; ein Projekt verwendet sie, sobald sie in seinen Einstellungen aktiviert ist.',
      sections: {
        'The catalogue': 'Der Katalog',
        'Add locale': 'Locale hinzufügen',
        Aliases: 'Aliase',
      },
      contexts: {
        'add-dialog': 'Klicken Sie rechts oben auf „Add locale“.',
        'aliases-dialog': 'Klicken Sie am Ende der Zeile einer Locale auf „Aliases“.',
      },
    },
    'sr-Latn-RS': {
      title: 'Locale-i',
      intro:
        'Svaki jezik koji Glossa poznaje, sa plural formama na koje se oslanjaju njegovi exporti i starim zapisima na koje i dalje odgovara. Locale dodat ovde je dostupan; projekat počinje da ga koristi kada se uključi u podešavanjima tog projekta.',
      sections: {
        'The catalogue': 'Katalog',
        'Add locale': 'Dodavanje locale-a',
        Aliases: 'Aliasi',
      },
      contexts: {
        'add-dialog': 'Kliknite na „Add locale“ gore desno.',
        'aliases-dialog': 'Kliknite na „Aliases“ na kraju reda locale-a.',
      },
    },
  },
};
